// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A drawer component for collecting serials for serialized parts.
 * Allows users to add, search, filter, and remove serials with
 * duplicate validation and mismatch warnings.
 *
 * For ship operations, displays an autocomplete dropdown with available
 * assets at the shipping location, filtered by product. Also supports
 * manual entry with real-time validation via the check-serial API.
 */
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { useCheckSerial } from '@/asset/hooks/use-check-serial';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import {
  ArrowBackOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
} from '@/constants/hardfin-standard-icons';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type FilterType = 'all' | 'valid' | 'invalid';

interface Props {
  /**
   * Whether the drawer is open.
   */
  isOpen: boolean;
  /**
   * Function to call when the drawer should be closed.
   */
  onClose: () => void;
  /**
   * The SKU of the part - used as unique identifier.
   */
  partSku: string;
  /**
   * The name of the part.
   */
  partName: string;
  /**
   * The quantity received for this part.
   */
  receivedQuantity: number;
  /**
   * The current list of serials.
   */
  serials: string[];
  /**
   * Callback fired when the serials list changes.
   */
  onSerialsChange: (serials: string[]) => void;
  /**
   * Unique identifier for the item (used to track which item's serials are being edited).
   */
  itemId: string;
  /**
   * Set of serials that have been validated as invalid by the backend.
   */
  invalidSerials?: Set<string>;
  /**
   * Set of all serials used across all products in the shipment (excluding current item's serials).
   * Used to prevent duplicate serials across different products.
   */
  globalSerials?: Set<string>;
  /**
   * The location ID to filter available assets by.
   */
  locationId?: string;
  /**
   * The product ID to filter available assets by.
   */
  productId?: string;
  /**
   * Pre-filtered list of available serials at the location for this product.
   * If provided, this will be used instead of fetching assets.
   */
  availableSerials?: string[];
}

/**
 * A drawer for collecting serials for serialized parts.
 * Features:
 * - Add serials with duplicate validation
 * - Search and filter serials
 * - Remove individual serials
 * - Warning when serial count doesn't match received quantity
 * - Real-time form state updates
 */
export function SerialCollectionDrawer(props: Props) {
  // Destructure props for stable references in callbacks
  const {
    onSerialsChange,
    receivedQuantity,
    isOpen,
    itemId,
    serials,
    invalidSerials,
    globalSerials,
    availableSerials: propAvailableSerials,
  } = props;

  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [inputError, setInputError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Hook for checking serial validity
  const { mutateAsync: checkSerialValidity, isPending: isCheckingSerial } =
    useCheckSerial();

  // Track which item we're currently editing
  const [currentItemId, setCurrentItemId] = useState<string | null>(null);

  // Local state for serials
  const [localSerials, setLocalSerials] = useState<string[]>([]);

  // Stable reference to propAvailableSerials to prevent unnecessary recalculations
  const stablePropAvailableSerials = useMemo(() => {
    return propAvailableSerials ?? [];
  }, [propAvailableSerials]);

  // Extract available serials (excluding already added serials)
  const availableSerials = useMemo(() => {
    if (stablePropAvailableSerials.length === 0) {
      return [];
    }
    const serialSet = new Set(localSerials.map((s) => s.toLowerCase()));
    return stablePropAvailableSerials.filter(
      (serial) => !serialSet.has(serial.toLowerCase())
    );
  }, [stablePropAvailableSerials, localSerials]);

  // Case-insensitive lookup set for O(1) duplicate checking
  const serialLookupRef = useState(() => new Set<string>())[0];

  // Duplicate serials tracking (local duplicates only)
  const [duplicateSerials, setDuplicateSerials] = useState<Set<string>>(
    new Set()
  );

  // Combined invalid serials (duplicates + backend-validated invalid)
  const allInvalidSerials = useMemo(() => {
    const combined = new Set(duplicateSerials);
    if (invalidSerials) {
      invalidSerials.forEach((serial) => combined.add(serial));
    }
    return combined;
  }, [duplicateSerials, invalidSerials]);

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize/reset serials state only when drawer opens or when switching items
  useEffect(() => {
    if (isOpen) {
      // Initialize if first open OR if we're editing a different item
      if (currentItemId !== itemId) {
        setLocalSerials(serials);
        serialLookupRef.clear();
        serials.forEach((serial) => {
          serialLookupRef.add(serial.toLowerCase());
        });
        setDuplicateSerials(new Set());
        setInputError(null);
        setInputValue('');
        setSearchQuery('');
        setFilter('all');
        setCurrentItemId(itemId);
      }
    } else {
      // Reset when drawer closes
      setCurrentItemId(null);
    }
  }, [isOpen, itemId, serials, currentItemId, serialLookupRef]);

  const handleAddSerial = useCallback(
    async (serialToAdd: string) => {
      const trimmedValue = serialToAdd.trim();

      if (!trimmedValue) {
        return;
      }

      // Check for duplicates using O(1) lookup
      const lowerCaseValue = trimmedValue.toLowerCase();
      if (serialLookupRef.has(lowerCaseValue)) {
        setInputError('Serials already exists');
        return;
      }

      // Check against global serials (serials from other products)
      if (globalSerials?.has(trimmedValue)) {
        setInputError('Serials is already used in another product');
        return;
      }

      // Check if serial is in the available list (from inventory)
      const isInInventory = availableSerials.some(
        (s) => s.toLowerCase() === lowerCaseValue
      );

      // If not in inventory, validate via API
      if (!isInInventory) {
        try {
          const asset = await checkSerialValidity(trimmedValue);
          if (!asset) {
            if (isMountedRef.current) {
              setInputError('Serial not found');
            }
            return;
          }
        } catch (error) {
          const apiError = handleAPIError(error as never);
          const curated = getUserFacingErrorMessage(apiError);
          const isFallback = curated === SUPPORT_FALLBACK_ERROR_MESSAGE;
          if (isFallback && !apiError.sentToSentry) {
            captureException(error);
          }
          if (isMountedRef.current) {
            setInputError(
              isFallback
                ? 'Error validating serial. Please try again or contact support.'
                : curated
            );
          }
          return;
        }
      }

      // Clear error and add serial to local state and notify parent
      if (!isMountedRef.current) return;

      setInputError(null);
      const updatedSerials = [...localSerials, trimmedValue];
      setLocalSerials(updatedSerials);
      serialLookupRef.add(lowerCaseValue);
      onSerialsChange(updatedSerials);
      setInputValue('');

      // Auto-focus the input after adding, unless we've reached or exceeded the received quantity
      const shouldRefocus = updatedSerials.length < receivedQuantity;

      if (shouldRefocus) {
        // Use setTimeout to ensure focus happens after React updates
        const timeoutId = setTimeout(() => {
          if (isMountedRef.current && inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
        // Cleanup is handled by isMountedRef check
        return () => clearTimeout(timeoutId);
      }
      // If we've reached the target, blur the input
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current && inputRef.current) {
          inputRef.current.blur();
        }
      }, 0);
      // Cleanup is handled by isMountedRef check
      return () => clearTimeout(timeoutId);
    },
    [
      localSerials,
      serialLookupRef,
      onSerialsChange,
      receivedQuantity,
      globalSerials,
      availableSerials,
      checkSerialValidity,
    ]
  );

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    setInputError(null);
  }, []);

  const handleRemoveSerial = useCallback(
    (serialToRemove: string) => {
      const newSerials = localSerials.filter(
        (serial) => serial !== serialToRemove
      );
      setLocalSerials(newSerials);
      serialLookupRef.delete(serialToRemove.toLowerCase());
      onSerialsChange(newSerials);

      // Remove from duplicates if it was marked as duplicate
      setDuplicateSerials((prev) => {
        if (prev.has(serialToRemove)) {
          const newDuplicates = new Set(prev);
          newDuplicates.delete(serialToRemove);
          return newDuplicates;
        }
        return prev;
      });
    },
    [localSerials, serialLookupRef, onSerialsChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && inputValue.trim()) {
        event.preventDefault();
        handleAddSerial(inputValue);
      }
    },
    [handleAddSerial, inputValue]
  );

  // Filter serials based on search query and filter type
  const filteredSerials = useMemo(
    () =>
      localSerials.filter((serial) => {
        const matchesSearch = serial
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

        if (filter === 'valid') {
          return matchesSearch && !allInvalidSerials.has(serial);
        }
        if (filter === 'invalid') {
          return matchesSearch && allInvalidSerials.has(serial);
        }
        return matchesSearch;
      }),
    [localSerials, searchQuery, filter, allInvalidSerials]
  );

  const validCount = useMemo(
    () => localSerials.filter((s) => !allInvalidSerials.has(s)).length,
    [localSerials, allInvalidSerials]
  );

  const invalidCount = allInvalidSerials.size;

  return (
    <Paper
      elevation={3}
      square
      sx={{
        width: 500,
        p: 3,
        height: '100%',
        overflowY: 'auto',
        borderLeft: 1,
        borderColor: 'divider',
      }}
    >
      <Stack gap={2}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">Serials for {props.partSku}</Typography>
          <IconButton onClick={props.onClose} size="small">
            <ArrowBackOutlined />
          </IconButton>
        </Stack>

        {/* Summary */}
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              {props.partName} to ship:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatNumber(receivedQuantity)}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Serials entered:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatNumber(localSerials.length)}
            </Typography>
          </Stack>
          <Box sx={{ mt: 1, textAlign: 'right' }}>
            {(() => {
              const serialCount = localSerials.length;
              const expectedQuantity = receivedQuantity;

              if (invalidCount > 0) {
                return (
                  <Typography variant="body2" sx={{ color: 'error.main' }}>
                    (
                    {pluralize(serialCount, {
                      singular: 'serial',
                      plural: 'serials',
                      includeCount: true,
                    })}{' '}
                    entered (
                    {pluralize(invalidCount, {
                      singular: 'invalid',
                      plural: 'invalid',
                      includeCount: true,
                    })}
                    ))
                  </Typography>
                );
              } else if (serialCount === 0 && expectedQuantity === 0) {
                return (
                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                    (Complete)
                  </Typography>
                );
              } else if (serialCount === 0) {
                return (
                  <Typography variant="body2" sx={{ color: 'error.main' }}>
                    (
                    {pluralize(expectedQuantity, {
                      singular: 'serial',
                      plural: 'serials',
                      includeCount: true,
                    })}{' '}
                    needed)
                  </Typography>
                );
              } else if (serialCount === expectedQuantity) {
                return (
                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                    (Complete)
                  </Typography>
                );
              } else if (serialCount > expectedQuantity) {
                const extra = serialCount - expectedQuantity;
                return (
                  <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                    (
                    {pluralize(extra, {
                      singular: 'extra serial',
                      plural: 'extra serials',
                      includeCount: true,
                    })}
                    )
                  </Typography>
                );
              }
              const needed = expectedQuantity - serialCount;
              return (
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  (
                  {pluralize(needed, {
                    singular: 'serial',
                    plural: 'serials',
                    includeCount: true,
                  })}{' '}
                  needed)
                </Typography>
              );
            })()}
          </Box>
        </Box>

        {/* Add serial input */}
        <Stack gap={1}>
          <Autocomplete
            fullWidth
            freeSolo
            value={null}
            options={
              availableSerials.length === 0
                ? [`No ${props.partSku}'s available`]
                : availableSerials
            }
            getOptionDisabled={(option) =>
              option === `No ${props.partSku}'s available`
            }
            inputValue={inputValue}
            onInputChange={(_, newValue) => {
              handleInputChange(newValue);
            }}
            onChange={(_, newValue) => {
              if (
                typeof newValue === 'string' &&
                newValue.trim() &&
                newValue !== `No ${props.partSku}'s available`
              ) {
                handleAddSerial(newValue);
              }
            }}
            loading={isCheckingSerial}
            disableCloseOnSelect
            forcePopupIcon
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select an asset"
                size="small"
                error={!!inputError}
                helperText={inputError}
                inputRef={inputRef}
                onKeyDown={handleKeyDown}
              />
            )}
            noOptionsText="No serials available at this location"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'right' }}
          >
            {`${availableSerials.length} ${pluralize(availableSerials.length, {
              singular: 'serial',
              plural: 'serials',
              includeCount: false,
            })} available at location`}
          </Typography>
        </Stack>

        {/* Filters and search */}
        <Stack direction="row" gap={2} alignItems="center">
          <Box sx={{ width: '66%' }}>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(_, newFilter) => {
                if (newFilter !== null) {
                  setFilter(newFilter);
                }
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="all">
                <Typography variant="button" textTransform="none">
                  All ({formatNumber(localSerials.length)})
                </Typography>
              </ToggleButton>
              <ToggleButton value="valid">
                <Typography variant="button" textTransform="none">
                  Valid ({formatNumber(validCount)})
                </Typography>
              </ToggleButton>
              <ToggleButton value="invalid">
                <Typography variant="button" textTransform="none">
                  Invalid ({formatNumber(invalidCount)})
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ width: '33%' }}>
            <TextField
              fullWidth
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Stack>

        {/* Serial list */}
        <Box>
          {filteredSerials.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                minHeight: '20vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No serials added yet
              </Typography>
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 2,
                maxHeight: filteredSerials.length > 30 ? '400px' : 'auto',
                overflowY: filteredSerials.length > 30 ? 'auto' : 'visible',
              }}
            >
              {filteredSerials.map((serial) => {
                const isValid = !allInvalidSerials.has(serial);
                return (
                  <Stack
                    key={serial}
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{
                      borderLeft: 3,
                      borderRadius: '5px',
                      borderColor: isValid ? 'success.main' : 'error.main',
                      pl: 1,
                      py: 0.5,
                      minWidth: 0,
                    }}
                  >
                    {isValid && (
                      <CheckOutlined
                        fontSize="small"
                        sx={{ color: 'success.main', flexShrink: 0 }}
                      />
                    )}
                    <ConditionalTooltip
                      tooltipText={serial.length > 11 ? serial : undefined}
                      arrow
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {serial}
                      </Typography>
                    </ConditionalTooltip>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveSerial(serial)}
                      sx={{ p: 0.5, flexShrink: 0 }}
                    >
                      <CloseOutlined fontSize="small" />
                    </IconButton>
                  </Stack>
                );
              })}
            </Box>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
